/**
 * categorieLavoro.ts — a chi interessa un cantiere (piano 8, 31/10/2026; doc PERMESSI-EDILIZI-MODEL §8).
 *
 * Decisione di Simone: un'azienda usa il modulo SOLO per i prodotti per cui la piattaforma l'ha abilitata (Alberto Podda,
 * che lavora in Vivisolar e in Fortezza, dall'account di Vivisolar non deve poterlo usare per infissi o coibentazione).
 * Ogni pratica si classifica UNA volta in categorie di lavoro (il modello «economico» del registro legge la parte pubblica:
 * `services/permessi/classificazione.ts`); ogni azienda ha i suoi prodotti del modulo (`permessi_aziende/{azienda}`), che
 * diventano categorie; questa regola, pura, decide «sì», «no» o «da verificare». Le categorie sono più fini dei temi delle
 * Gare: «efficienza energetica» metteva insieme fotovoltaico e coibentazione, che qui sono di aziende diverse.
 *
 * La regola (misurata sulle 66 pratiche vere del 31/10/2026, arbitro Opus 5.5):
 * - casa nuova o ristrutturazione → a tutte le aziende che hanno almeno una categoria: i documenti dicono quasi sempre
 *   solo «ristrutturazione di un fabbricato», e nessuno può sapere se le finestre si cambieranno;
 * - un lavoro di un solo prodotto → solo a chi lo vende;
 * - ciò che non riguarda edifici (pozzi, cartelli, antenne…) → a nessuno;
 * - non ancora classificata, o il modello non ha trovato niente → «da verificare», a tutti: meglio un cantiere in più che
 *   uno perso;
 * - i segnali certi del documento valgono sempre (requisiti acustici → infissi, rimozione dell'amianto → tetto).
 *
 * Codice condiviso fra browser e funzioni: puro, niente oltre ES2017.
 */
import type { IstanteIso } from '../gare/tipi';
import type { IdSegnale } from './vocabolario';

export type IdCategoriaLavoro =
  | 'nuova_costruzione'
  | 'ristrutturazione'
  | 'infissi'
  | 'coibentazione'
  | 'tetto'
  | 'fotovoltaico'
  | 'clima_acqua_calda'
  | 'ricarica_veicoli'
  | 'esterni'
  | 'non_edifici';

/** Le categorie che sono lavori di un edificio intero: interessano a chi vende qualunque cosa per la casa. */
const DI_TUTTO_L_EDIFICIO: readonly IdCategoriaLavoro[] = ['nuova_costruzione', 'ristrutturazione'];

/**
 * Le categorie e come le legge il modello. Il testo è in italiano come nella prova sui dati veri (66 pratiche, 6 risposte
 * su 660 diverse dall'arbitro): cambiarlo vuol dire rifare il confronto (`functions/scripts/permessi/confronta-classificazione.ts`)
 * e alzare `VERSIONE_CLASSIFICAZIONE`. Le etichette per le persone stanno in i18n (`permessi:categorie_lavoro.<id>`).
 */
export const CATEGORIE_LAVORO: readonly { id: IdCategoriaLavoro; per_il_modello: string }[] = [
  { id: 'nuova_costruzione', per_il_modello: 'casa o edificio nuovo' },
  { id: 'ristrutturazione', per_il_modello: 'ristrutturazione, ampliamento o cambio d’uso di un edificio' },
  { id: 'infissi', per_il_modello: 'infissi (finestre, porte, persiane, tapparelle)' },
  { id: 'coibentazione', per_il_modello: 'coibentazione (cappotto, isolamento)' },
  { id: 'tetto', per_il_modello: 'tetto o copertura (anche amianto)' },
  { id: 'fotovoltaico', per_il_modello: 'fotovoltaico o accumulo' },
  { id: 'clima_acqua_calda', per_il_modello: 'clima, riscaldamento, acqua calda' },
  { id: 'ricarica_veicoli', per_il_modello: 'ricarica di auto elettriche' },
  { id: 'esterni', per_il_modello: 'cancelli, recinzioni, ringhiere, pergole, tende' },
  { id: 'non_edifici', per_il_modello: 'non riguarda un edificio (pozzi, acque, antenne, strade, cartelli, distributori, campi)' },
];

/** Sale a ogni cambio delle categorie o delle istruzioni al modello: le pratiche si riclassificano. */
export const VERSIONE_CLASSIFICAZIONE = 'classifica-1';

/**
 * Quali prodotti (`PRODUCT_TYPES_MAP`) portano a quale categoria. Le colonnine vanno a chi vende fotovoltaico (Vivisolar le
 * vende dentro il fotovoltaico); il tetto a chi monta pannelli e a chi coibenta.
 */
export const PRODOTTI_PER_CATEGORIA: Readonly<Record<Exclude<IdCategoriaLavoro, 'nuova_costruzione' | 'ristrutturazione' | 'non_edifici'>, readonly string[]>> = {
  fotovoltaico: ['FOTOVOLTAICO', 'POSA_FV_TERZI', 'ASSISTENZA_FV'],
  ricarica_veicoli: ['FOTOVOLTAICO'],
  clima_acqua_calda: ['CLIMATIZZAZIONE', 'CALDAIE', 'ACQUA_CALDA_SANITARIA', 'CHILLER'],
  infissi: [
    'INFISSI_ALLUMINIO',
    'INFISSI_PVC',
    'INFISSI_LEGNO',
    'PORTE_INTERNE',
    'PERSIANE',
    'ZANZARIERE',
    'CONTROTELAI',
    'PORTE_BLINDATE',
    'PORTONI_BASCULANTI',
    'TAPPARELLE_AVVOLGIBILI',
    'VETRATE_PANORAMICHE',
    'MANUTENZIONE_INFISSI',
  ],
  coibentazione: ['FIOCCO_TERMICO'],
  tetto: ['FOTOVOLTAICO', 'POSA_FV_TERZI', 'FIOCCO_TERMICO'],
  esterni: ['CANCELLI_RINGHIERE', 'PERGOLE', 'TENDE_DA_SOLE', 'BALAUSTRE_VETRO'],
};

/** Come il modello ha classificato una pratica: sulla pratica, insieme ai campi da cui l'ha letta. */
export interface ClassificazionePratica {
  categorie: IdCategoriaLavoro[];
  versione: string;
  /** Il modello che ha risposto, dal registro. */
  modello: string;
  /** La pubblicazione da cui venivano i campi letti (`SchedaPratica.campi_da`): per sapere da dove, non per decidere. */
  campi_da: string;
  /**
   * L'impronta del testo che il modello ha letto (`testoDaClassificare`, calcolata sul server): la classificazione vale
   * finché quel testo non cambia. Non `campi_da`: lo stesso atto riletto da un lettore migliore può dire altro, e un atto
   * nuovo può dire la stessa cosa (revisione del 31/10/2026).
   */
  impronta: string;
  il: IstanteIso;
}

export type Interesse = 'si' | 'no' | 'da_verificare';

const SEGNALI_CERTI: Partial<Record<IdSegnale, IdCategoriaLavoro>> = { requisiti_acustici: 'infissi', rimozione_amianto: 'tetto' };

const ID_NOTI = CATEGORIE_LAVORO.map((c) => c.id);

/** Le categorie di un'azienda dai prodotti del modulo; i prodotti che non c'entrano coi cantieri non portano niente. */
export function categorieDeiProdotti(prodotti: readonly string[]): IdCategoriaLavoro[] {
  const out: IdCategoriaLavoro[] = [];
  for (const id of Object.keys(PRODOTTI_PER_CATEGORIA) as (keyof typeof PRODOTTI_PER_CATEGORIA)[]) {
    if (PRODOTTI_PER_CATEGORIA[id].some((p) => prodotti.indexOf(p) >= 0)) out.push(id);
  }
  return out;
}

/** Le categorie di una pratica: quelle del modello più i segnali certi del documento. Null se non è ancora classificata. */
export function categorieDellaPratica(c: ClassificazionePratica | null | undefined, segnali: readonly IdSegnale[]): IdCategoriaLavoro[] | null {
  if (!c) return null;
  const out = c.categorie.filter((x) => ID_NOTI.indexOf(x) >= 0);
  for (const s of segnali) {
    const dal = SEGNALI_CERTI[s];
    if (dal && out.indexOf(dal) < 0) out.push(dal);
  }
  return out;
}

/** La regola: interessa all'azienda con queste categorie una pratica con queste categorie (null = non classificata)? */
export function interesseDellAzienda(categorie: readonly IdCategoriaLavoro[] | null, dellAzienda: readonly IdCategoriaLavoro[]): Interesse {
  if (!categorie || !categorie.length) return 'da_verificare';
  if (!dellAzienda.length) return 'no';
  if (categorie.some((c) => DI_TUTTO_L_EDIFICIO.indexOf(c) >= 0)) return 'si';
  return categorie.some((c) => dellAzienda.indexOf(c) >= 0) ? 'si' : 'no';
}

/** Dalla risposta del modello: solo le categorie note col valore `true`. Mai una categoria inventata. */
/** La parte pubblica della pratica che il modello legge, in cinque righe: mai lo spazio protetto, mai il committente. */
export function testoDaClassificare(p: {
  tipo_atto: string;
  tipologie: readonly { codice: string; testo: string }[];
  endoprocedimenti: readonly { codice: string; testo: string }[];
  destinazione: string | null;
  lavori: string | null;
}): string {
  return [
    `Tipo di atto: ${p.tipo_atto}`,
    `Tipo di intervento (codici della Regione): ${p.tipologie.map((t) => `${t.codice} ${t.testo}`).join('; ') || 'non indicato'}`,
    `Altri permessi nella pratica: ${p.endoprocedimenti.map((e) => `${e.codice} ${e.testo}`).join('; ') || 'nessuno'}`,
    `Destinazione d'uso: ${p.destinazione || 'non indicata'}`,
    `Descrizione dei lavori: ${p.lavori || 'non indicata'}`,
  ].join('\n');
}

/**
 * Una risposta del modello che si può scrivere: un oggetto con almeno una categoria nota, e ogni categoria nota presente
 * con un sì o un no veri. `{}`, una forma diversa o «"true"» fra virgolette no: si ritenta, invece di salvare per sempre
 * una classificazione vuota che sembra un «tutto no» (revisione del 31/10/2026).
 */
export function rispostaValida(grezze: unknown): boolean {
  if (!grezze || typeof grezze !== 'object' || Array.isArray(grezze)) return false;
  const r = grezze as Record<string, unknown>;
  const presenti = ID_NOTI.filter((id) => id in r);
  return presenti.length > 0 && presenti.every((id) => typeof r[id] === 'boolean');
}

export function categorieValide(grezze: unknown): IdCategoriaLavoro[] {
  if (!grezze || typeof grezze !== 'object' || Array.isArray(grezze)) return [];
  const r = grezze as Record<string, unknown>;
  return ID_NOTI.filter((id) => r[id] === true);
}
